import type { Question } from '../../types';

// ---------------------------------------------------------------------------
// Manufactured Housing Community (MH Park) Supplement
// Source: Centered Partners revised client intake (2026-06-11), Tab 4
// "Manufactured Housing". Items not found in any insurance document that
// materially affect whether and how coverage can be placed.
// All questions conditioned on industry_type eq 'manufactured_housing'.
//
// Staffing, pets, evictions, amenities, rent collection, and money handling
// are asked in the shared operations profile (operations-profile.ts).
// Habitability / crime history remain agent-mode underwriting questions.
// ---------------------------------------------------------------------------

const MH_CONDITION = [
  { field: 'industry_type', operator: 'eq' as const, value: 'manufactured_housing' },
];

export const manufacturedHousingQuestions: Question[] = [
  // ---------------------------------------------------------------------
  // Quick community grid — one row per MH community
  // ---------------------------------------------------------------------
  {
    id: 'mh_communities',
    text: 'Quick community grid — one entry per MH community',
    subtext:
      'Approximate answers are fine. Park-owned home (POH) counts matter — POHs need scheduled property coverage.',
    section: 'supplement',
    group: 'mh_inventory',
    sort_order: 500,
    field_type: 'repeating_group',
    required: true,
    field_path: 'supplement.mh.communities',
    conditions: MH_CONDITION,
    repeating_group: {
      min_rows: 1,
      max_rows: 50,
      add_button_label: 'Add another community',
      row_label: 'Community',
      fields: [
        {
          id: 'mh_c_name',
          text: 'Community name',
          section: 'supplement',
          sort_order: 10,
          field_type: 'text',
          placeholder: 'Oak Meadows MHC',
          required: true,
          field_path: 'community_name',
          conditions: [],
        },
        {
          id: 'mh_c_total_pads',
          text: 'Total pads',
          section: 'supplement',
          sort_order: 20,
          field_type: 'number',
          placeholder: '120',
          required: true,
          field_path: 'total_pads',
          conditions: [],
        },
        {
          id: 'mh_c_occupied_pads',
          text: 'Occupied pads',
          section: 'supplement',
          sort_order: 30,
          field_type: 'number',
          placeholder: '110',
          required: true,
          field_path: 'occupied_pads',
          conditions: [],
        },
        {
          id: 'mh_c_poh_count',
          text: 'Park-owned homes (#)',
          subtext: 'Community owns the home; rents home + pad.',
          section: 'supplement',
          sort_order: 40,
          field_type: 'number',
          placeholder: '0',
          required: true,
          field_path: 'poh_count',
          conditions: [],
        },
        {
          id: 'mh_c_toh_count',
          text: 'Tenant-owned homes (#)',
          subtext: 'Resident owns the home; rents only the pad.',
          section: 'supplement',
          sort_order: 50,
          field_type: 'number',
          placeholder: '110',
          required: true,
          field_path: 'toh_count',
          conditions: [],
        },
        {
          id: 'mh_c_pre1976_pct',
          text: 'Approx % of homes built before 1976',
          subtext: 'Pre-HUD-code homes are increasingly difficult to insure.',
          section: 'supplement',
          sort_order: 60,
          field_type: 'percentage',
          placeholder: '0',
          required: true,
          field_path: 'pre_1976_pct',
          conditions: [],
        },
      ],
    },
    tags: ['supplement', 'mh', 'repeating'],
  },

  // ---------------------------------------------------------------------
  // Community details
  // ---------------------------------------------------------------------
  {
    id: 'mh_anchoring',
    text: 'Are homes anchored / tied down to current state code?',
    subtext: 'For example, HUD anchoring standards.',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 510,
    field_type: 'select',
    options: [
      { label: 'Yes — all homes anchored to code', value: 'all_anchored' },
      { label: 'Partially — some homes anchored', value: 'partial' },
      { label: 'No', value: 'none' },
      { label: 'Unknown / needs verification', value: 'unknown' },
    ],
    required: true,
    field_path: 'supplement.mh.anchoring',
    conditions: MH_CONDITION,
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_foundations',
    text: 'Foundations: permanent or temporary?',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 520,
    field_type: 'select',
    options: [
      { label: 'Permanent (block, slab)', value: 'permanent' },
      { label: 'Temporary (piers, blocks)', value: 'temporary' },
      { label: 'Mixed', value: 'mixed' },
    ],
    required: true,
    field_path: 'supplement.mh.foundations',
    conditions: MH_CONDITION,
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_water_source',
    text: 'Water source: municipal, private well, or both?',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 530,
    field_type: 'select',
    options: [
      { label: 'Municipal', value: 'municipal' },
      { label: 'Private well', value: 'well' },
      { label: 'Both', value: 'both' },
    ],
    required: true,
    field_path: 'supplement.mh.water_source',
    conditions: MH_CONDITION,
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_well_test_date',
    text: 'Date of the last well potability test',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 540,
    field_type: 'date',
    required: true,
    field_path: 'supplement.mh.well_test_date',
    conditions: [
      ...MH_CONDITION,
      { field: 'mh_water_source', operator: 'in', value: ['well', 'both'] },
    ],
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_sewer',
    text: 'Sewer: municipal, septic, or lagoon?',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 550,
    field_type: 'select',
    options: [
      { label: 'Municipal', value: 'municipal' },
      { label: 'Septic', value: 'septic' },
      { label: 'Lagoon', value: 'lagoon' },
      { label: 'Mixed', value: 'mixed' },
    ],
    required: true,
    field_path: 'supplement.mh.sewer',
    conditions: MH_CONDITION,
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_septic_inspection',
    text: 'Last septic / lagoon inspection or pump-out date',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 560,
    field_type: 'date',
    required: true,
    field_path: 'supplement.mh.septic_inspection_date',
    conditions: [
      ...MH_CONDITION,
      { field: 'mh_sewer', operator: 'in', value: ['septic', 'lagoon', 'mixed'] },
    ],
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_usts',
    text: 'Underground storage tanks on site — now or in the past?',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 570,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.mh.usts',
    conditions: MH_CONDITION,
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_ust_detail',
    text: 'Are closure documents available? Describe the tanks.',
    subtext: 'Registration status, contents, closure docs if removed.',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 580,
    field_type: 'textarea',
    required: true,
    field_path: 'supplement.mh.ust_detail',
    conditions: [...MH_CONDITION, { field: 'mh_usts', operator: 'eq', value: true }],
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_propane',
    text: 'Propane: above-ground or buried tanks?',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 590,
    field_type: 'select',
    options: [
      { label: 'No propane tanks', value: 'none' },
      { label: 'Above-ground', value: 'above_ground' },
      { label: 'Buried', value: 'buried' },
      { label: 'Both', value: 'both' },
    ],
    required: true,
    field_path: 'supplement.mh.propane',
    conditions: MH_CONDITION,
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_propane_detail',
    text: 'Who maintains the propane tanks?',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 600,
    field_type: 'text',
    placeholder: 'AmeriGas — annual inspection',
    required: true,
    field_path: 'supplement.mh.propane_detail',
    conditions: [...MH_CONDITION, { field: 'mh_propane', operator: 'neq', value: 'none' }],
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_roads',
    text: 'Roads: paved or gravel — owned by you or the municipality?',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 610,
    field_type: 'select',
    options: [
      { label: 'Paved — owned by community', value: 'paved_community' },
      { label: 'Gravel — owned by community', value: 'gravel_community' },
      { label: 'Owned / maintained by municipality', value: 'municipal' },
      { label: 'Mixed', value: 'mixed' },
    ],
    required: true,
    field_path: 'supplement.mh.roads',
    conditions: MH_CONDITION,
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_storm_shelter',
    text: 'Storm or tornado shelter on site?',
    subtext: 'Required in some states for manufactured housing communities.',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 620,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.mh.storm_shelter',
    conditions: MH_CONDITION,
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_utilities',
    text: 'Utility billing: master-metered, sub-metered (RUBS), or paid directly by residents?',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 630,
    field_type: 'select',
    options: [
      { label: 'Master-metered (community pays)', value: 'master_metered' },
      { label: 'Sub-metered / RUBS', value: 'sub_metered' },
      { label: 'Paid directly by residents', value: 'direct' },
      { label: 'Mixed', value: 'mixed' },
    ],
    required: true,
    field_path: 'supplement.mh.utilities',
    conditions: MH_CONDITION,
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_pad_rent_avg',
    text: 'Average pad rent per month',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 640,
    field_type: 'currency',
    placeholder: '450',
    required: true,
    field_path: 'supplement.mh.pad_rent_avg',
    conditions: MH_CONDITION,
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_rent_includes',
    text: 'What is included in lot rent?',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 650,
    field_type: 'multiselect',
    options: [
      { label: 'Water', value: 'water' },
      { label: 'Sewer', value: 'sewer' },
      { label: 'Trash', value: 'trash' },
      { label: 'Lawn care', value: 'lawn' },
      { label: 'None — lot rent only', value: 'none' },
    ],
    required: true,
    field_path: 'supplement.mh.rent_includes',
    conditions: MH_CONDITION,
    tags: ['supplement', 'mh'],
  },
  {
    id: 'mh_lot_improvements',
    text: 'Resident-built structures (decks, sheds, carports): owned by the resident or the community?',
    section: 'supplement',
    group: 'mh_details',
    sort_order: 660,
    field_type: 'select',
    options: [
      { label: 'Owned by residents', value: 'resident_owned' },
      { label: 'Owned by community', value: 'community_owned' },
      { label: 'Mixed', value: 'mixed' },
      { label: 'No resident-built structures', value: 'none' },
    ],
    required: true,
    field_path: 'supplement.mh.lot_improvements',
    conditions: MH_CONDITION,
    tags: ['supplement', 'mh'],
  },

  // ---------------------------------------------------------------------
  // Agent-mode underwriting history (dropped from the client-facing form;
  // still valuable — gathered on the call or from documents)
  // ---------------------------------------------------------------------
  {
    id: 'mh_habitability_claims',
    text: 'Any habitability claims?',
    section: 'supplement',
    group: 'mh_history',
    sort_order: 670,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.mh.habitability_claims',
    conditions: MH_CONDITION,
    modes: ['agent'],
    tags: ['supplement', 'mh', 'agent'],
  },
  {
    id: 'mh_habitability_detail',
    text: 'Describe the habitability claims',
    section: 'supplement',
    group: 'mh_history',
    sort_order: 680,
    field_type: 'textarea',
    required: true,
    field_path: 'supplement.mh.habitability_detail',
    conditions: [...MH_CONDITION, { field: 'mh_habitability_claims', operator: 'eq', value: true }],
    modes: ['agent'],
    tags: ['supplement', 'mh', 'agent'],
  },
  {
    id: 'mh_crime_incidents',
    text: 'Any criminal incidents on community grounds in the last 3 years?',
    section: 'supplement',
    group: 'mh_history',
    sort_order: 690,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.mh.crime_incidents',
    conditions: MH_CONDITION,
    modes: ['agent'],
    tags: ['supplement', 'mh', 'agent'],
  },
  {
    id: 'mh_crime_detail',
    text: 'Describe the incidents and area crime score if known',
    subtext: 'CrimeMapping or similar — last 12-month incident counts within 1 mile.',
    section: 'supplement',
    group: 'mh_history',
    sort_order: 700,
    field_type: 'textarea',
    required: true,
    field_path: 'supplement.mh.crime_detail',
    conditions: [...MH_CONDITION, { field: 'mh_crime_incidents', operator: 'eq', value: true }],
    modes: ['agent'],
    tags: ['supplement', 'mh', 'agent'],
  },
];
